import { memo, useMemo } from 'react';

import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/shadcn/select';

import type { MultiSelectOption } from '@/components/forms/components/form/writer/widgets/multiSelectMenu/multiSelectMenu.writer';
import type { WidgetEditorBaseProps } from '@/components/ui/widget/views/text/text.view.type';

export type SelectDropdownProps = WidgetEditorBaseProps<string> & {
  options?: string[] | MultiSelectOption[];
};

// simple select input
export const SelectDropdown = memo((props: SelectDropdownProps) => {
  const isInBatchEdit = useMemo(() => !!props.header, [props.header]);
  if (!Array.isArray(props.options)) {
    console.error('SelectDropdown editor: options must be an array');
    return null;
  }

  const dropDownOptions = useMemo(() => {
    let unifiedOptions: MultiSelectOption[] = [];
    if (optionIsAnArrayOfObjects(props.options)) {
      unifiedOptions = props.options;
    } else {
      unifiedOptions = (props.options ?? []).map(option => ({
        label: option,
        value: option
      }));
    }

    // Some dropdowns can have current values that we don't want the user to select
    // (e.g. "Valid (from TA)" in the AGRe FF workflow).
    // We therefore add it as an option to make it visible in the dropdown.
    if (
      !!props.value &&
      !unifiedOptions.find(({ value }) => value === props.value)
    ) {
      unifiedOptions.unshift({
        label: props.value,
        value: props.value
      });
    }
    return unifiedOptions;
  }, [props.value, props.options]);

  return (
    <div className={cn('flex flex-col gap-2', !isInBatchEdit && 'size-full')}>
      {isInBatchEdit && (
        <Label
          data-cy={`select-editor-${props.identifier}-label`}
          htmlFor={props.identifier}
        >
          {props.header}
        </Label>
      )}
      <Select
        disabled={props.disabled}
        onValueChange={props.onChange}
        value={props.value}
      >
        <SelectTrigger
          className={cn(
            'size-full cursor-pointer bg-transparent hover:bg-accent',
            isInBatchEdit
              ? 'border-input'
              : 'rounded-none border-0 border-transparent focus:ring-0'
          )}
          data-cy={`select-editor-${props.identifier}-button`}
          id={props.identifier}
        >
          <SelectValue
            data-cy={`select-editor-${props.identifier}-value`}
            placeholder={props.placeholder ?? 'Select an option'}
          />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {dropDownOptions.map(option => (
              <SelectItem
                data-cy={`select-editor-${props.identifier}-item-${option.value}`}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
});

const optionIsAnArrayOfObjects = (
  options: string[] | MultiSelectOption[] | undefined
): options is MultiSelectOption[] => {
  if (!options) return false;
  return options.length > 0 && typeof options[0] === 'object';
};
